import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CalendarDays, Search, Clock, Play, CheckCircle2, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

const StatusBadge = ({ status }) => {
  const cfg = {
    scheduled: { label: 'Scheduled', icon: Clock, className: 'bg-[rgba(59,130,246,0.15)] text-[#3b82f6] border-[rgba(59,130,246,0.35)]' },
    live: { label: 'LIVE', icon: Play, className: 'bg-[rgba(239,68,68,0.15)] text-[#ef4444] border-[rgba(239,68,68,0.35)]' },
    finished: { label: 'Finished', icon: CheckCircle2, className: 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]' },
    completed: { label: 'Finished', icon: CheckCircle2, className: 'bg-[rgba(34,197,94,0.15)] text-[#22c55e] border-[rgba(34,197,94,0.35)]' },
  };
  const c = cfg[status?.toLowerCase()] || cfg.scheduled;
  const Icon = c.icon;
  return (
    <Badge className={`${c.className} border flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {c.label}
    </Badge>
  );
};

const MatchCard = ({ match }) => {
  // Handle both fixture format (home/away) and scorer format (team1/team2/home_team/away_team)
  const team1 = match.home || match.team1 || match.home_team || {};
  const team2 = match.away || match.team2 || match.away_team || {};
  const score1 = match.score1 ?? match.home_score ?? match.score?.home ?? '–';
  const score2 = match.score2 ?? match.away_score ?? match.score?.away ?? '–';
  const matchId = match.match_id || match.id;
  const isLive = match.status?.toLowerCase() === 'live';

  return (
    <Card className={`bg-[#161a26] border-[#252a37] hover:border-[rgba(245,197,24,0.35)] transition-colors ${
      isLive ? 'ring-1 ring-[rgba(239,68,68,0.25)]' : ''
    }`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <StatusBadge status={match.status} />
          <span className="text-xs text-[#8a93a3]">
            {match.scheduled_at ? new Date(match.scheduled_at).toLocaleString() : 'TBD'}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex-1 text-center">
            <div className="h-12 w-12 rounded-full bg-[#1d2230] mx-auto mb-2 flex items-center justify-center">
              {team1.logo_url
                ? <img src={team1.logo_url} alt={team1.name} className="h-10 w-10 rounded-full object-cover" />
                : <span className="text-[#f5c518] font-bold">{team1.short_name?.[0] || team1.name?.[0] || 'T1'}</span>
              }
            </div>
            <p className="text-sm font-medium text-[#f0f2f7] truncate">{team1.short_name || team1.name || 'Home'}</p>
          </div>

          <div className={`px-4 text-2xl font-bold tabular-nums ${isLive ? 'text-[#f5c518]' : 'text-[#f0f2f7]'}`}>
            {score1} – {score2}
          </div>

          <div className="flex-1 text-center">
            <div className="h-12 w-12 rounded-full bg-[#1d2230] mx-auto mb-2 flex items-center justify-center">
              {team2.logo_url
                ? <img src={team2.logo_url} alt={team2.name} className="h-10 w-10 rounded-full object-cover" />
                : <span className="text-[#f5c518] font-bold">{team2.short_name?.[0] || team2.name?.[0] || 'T2'}</span>
              }
            </div>
            <p className="text-sm font-medium text-[#f0f2f7] truncate">{team2.short_name || team2.name || 'Away'}</p>
          </div>
        </div>

        {(match.sport || match.category || match.tournament_name) && (
          <div className="mt-3 pt-3 border-t border-[#252a37] flex items-center justify-between">
            <p className="text-xs text-[#8a93a3] truncate flex-1">
              {[match.tournament_name, match.sport, match.category].filter(Boolean).join(' · ')}
            </p>
            {(match.status === 'live' || match.status === 'scheduled') && matchId && (
              <Link to={`/score/${matchId}`}>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-[#f5c518] hover:text-[#f7d24a] px-2 flex-shrink-0">
                  <Target className="h-3 w-3 mr-1" />
                  Score
                </Button>
              </Link>
            )}
          </div>
        )}
        {/* Show Score link even if no sport/category info */}
        {!(match.sport || match.category || match.tournament_name) && (match.status === 'live' || match.status === 'scheduled') && matchId && (
          <div className="mt-3 pt-3 border-t border-[#252a37] flex justify-end">
            <Link to={`/score/${matchId}`}>
              <Button size="sm" variant="ghost" className="h-7 text-xs text-[#f5c518] hover:text-[#f7d24a] px-2">
                <Target className="h-3 w-3 mr-1" />
                Score
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function MatchesPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const [searchParams] = useSearchParams();
  const defaultTournament = searchParams.get('tournament') || 'all';

  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [tournamentFilter, setTournamentFilter] = useState(defaultTournament);

  useEffect(() => {
    fetchTournaments();
  }, [orgSlug]);

  useEffect(() => {
    fetchMatches();
  }, [activeOrgSlug, tournamentFilter]);

  const fetchTournaments = async () => {
    try {
      const res = await api.get(`/orgs/${orgSlug}/tournaments`);
      setTournaments(Array.isArray(res.data) ? res.data : []);
    } catch (_) {}
  };

  const fetchMatches = async () => {
    setLoading(true);
    try {
      const toFetch = tournamentFilter === 'all'
        ? (await api.get(`/orgs/${orgSlug}/tournaments`).then(r => Array.isArray(r.data) ? r.data : []))
        : [{ id: tournamentFilter }];

      const all = await Promise.allSettled(
        toFetch.map((t) =>
          api.get(`/orgs/${orgSlug}/tournaments/${t.id}/fixtures`).then((r) => {
            const fixtures = Array.isArray(r.data) ? r.data : r.data.items || [];
            return fixtures.map((f) => ({ ...f, tournament_name: t.name || '' }));
          })
        )
      );
      const merged = all.flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
      setMatches(merged);
    } catch {
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const filteredMatches = matches.filter((m) => {
    const matchesSearch =
      m.team1?.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.team2?.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.home_team?.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.away_team?.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.tournament_name?.toLowerCase().includes(search.toLowerCase());
    if (activeTab === 'all') return search ? matchesSearch : true;
    const status = m.status?.toLowerCase();
    const tabMatch = activeTab === 'finished' ? (status === 'finished' || status === 'completed') : status === activeTab;
    return tabMatch && (search ? matchesSearch : true);
  });

  const counts = {
    all: matches.length,
    live: matches.filter((m) => m.status?.toLowerCase() === 'live').length,
    scheduled: matches.filter((m) => m.status?.toLowerCase() === 'scheduled').length,
    finished: matches.filter((m) => ['finished', 'completed'].includes(m.status?.toLowerCase())).length,
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-matches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#f0f2f7] mb-1">Matches</h1>
          <p className="text-[#8a93a3]">View tournament fixtures and results</p>
        </div>
        <Badge className="bg-[#1d2230] text-[#8a93a3] border border-[#252a37] w-fit">
          {matches.length} matches
        </Badge>
      </div>

      {/* Filters row */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by team name..."
            className="pl-10 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
          />
        </div>
        {tournaments.length > 0 && (
          <Select value={tournamentFilter} onValueChange={setTournamentFilter}>
            <SelectTrigger className="w-[200px] bg-[#161a26] border-[#252a37] text-[#f0f2f7]">
              <SelectValue placeholder="All tournaments" />
            </SelectTrigger>
            <SelectContent className="bg-[#161a26] border-[#252a37]">
              <SelectItem value="all" className="text-[#f0f2f7] focus:bg-[#1d2230]">All tournaments</SelectItem>
              {tournaments.map((t) => (
                <SelectItem key={t.id} value={t.id} className="text-[#f0f2f7] focus:bg-[#1d2230]">
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6" data-testid="matches-status-tabs">
        <TabsList className="bg-[#161a26] border border-[#252a37]">
          <TabsTrigger value="all"       className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#f0f2f7]">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="live"      className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#ef4444]">Live ({counts.live})</TabsTrigger>
          <TabsTrigger value="scheduled" className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#3b82f6]">Scheduled ({counts.scheduled})</TabsTrigger>
          <TabsTrigger value="finished"  className="data-[state=active]:bg-[#1d2230] data-[state=active]:text-[#22c55e]">Finished ({counts.finished})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Matches Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="bg-[#161a26] border-[#252a37]">
              <CardContent className="p-4">
                <Skeleton className="h-6 w-20 bg-[#1d2230] mb-4" />
                <div className="flex items-center justify-between">
                  <Skeleton className="h-12 w-12 rounded-full bg-[#1d2230]" />
                  <Skeleton className="h-8 w-16 bg-[#1d2230]" />
                  <Skeleton className="h-12 w-12 rounded-full bg-[#1d2230]" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredMatches.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="matches-grid">
          {filteredMatches.map((match) => (
            <MatchCard key={match.id} match={match} />
          ))}
        </div>
      ) : (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-12 text-center">
            <CalendarDays className="h-12 w-12 text-[#8a93a3] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#f0f2f7] mb-2">
              {search || activeTab !== 'all' ? 'No matches found' : 'No matches yet'}
            </h3>
            <p className="text-[#8a93a3]">
              {search || activeTab !== 'all'
                ? 'Try adjusting your filters'
                : 'Generate fixtures from the Tournaments page to see matches here'}
            </p>
            {!search && activeTab === 'all' && (
              <Button asChild className="mt-4 bg-[#f5c518] text-[#0b0f18] hover:bg-[#f7d24a]">
                <Link to="/admin/tournaments">Go to Tournaments</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
